import { useMemo, useState } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import { TextInput } from '../../Common/TextInput'
import { Button } from '../../Common/Button'
import { useFormDefinitions } from '../../../hooks/useFormDefinitions'
import { useFormResponses } from '../../../hooks/useFormResponses'
import {
  formDefinitionToDataContext,
  formResponsesToDataContext,
} from '../workspaceAssetContext'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'
import type { FormDefinition, FormResponse } from '../../../types/forms'

export interface FormsResponsesPaneProps {
  workspaceId: string
  basket: DataContext[]
  onToggle: (context: DataContext) => void
  mode?: 'aiContext' | 'itemAttachment'
  onAttachForm?: (formId: string) => Promise<void>
  onDetachForm?: (formId: string) => Promise<void>
  attachedFormIds?: Set<string>
}

type Step = 'list' | 'responses'

export const FormsResponsesPane = ({
  workspaceId,
  basket,
  onToggle,
  mode = 'aiContext',
  onAttachForm,
  onDetachForm,
  attachedFormIds,
}: FormsResponsesPaneProps) => {
  const { t } = useNls()
  const [search, setSearch] = useState('')
  const [step, setStep] = useState<Step>('list')
  const [activeDrillForm, setActiveDrillForm] = useState<FormDefinition | null>(null)
  const [responseSearch, setResponseSearch] = useState('')
  const [selectedResponseIds, setSelectedResponseIds] = useState<Set<string>>(new Set())

  const basketIdSet = useMemo(() => new Set(basket.map((item) => item.id)), [basket])

  const formsPage = useFormDefinitions({
    workspaceId,
    lifecycleStatus: 'ALL',
    search,
    sort: 'NEWEST',
    cursor: null,
  })

  const responsesState = useFormResponses({
    workspaceId,
    formId: activeDrillForm?.id,
    search: responseSearch,
    limit: 50,
  })

  const filteredForms = useMemo(() => formsPage.items, [formsPage.items])

  const handleDrillIn = (form: FormDefinition) => {
    setActiveDrillForm(form)
    setResponseSearch('')
    setSelectedResponseIds(new Set())
    setStep('responses')
  }

  const handleBack = () => {
    setStep('list')
    setActiveDrillForm(null)
    setResponseSearch('')
    setSelectedResponseIds(new Set())
  }

  const handleAttachFormOnly = (form: FormDefinition) => {
    const context = formDefinitionToDataContext(form)
    onToggle(context)
  }

  const toggleResponse = (response: FormResponse) => {
    setSelectedResponseIds((prev) => {
      const next = new Set(prev)
      if (next.has(response.id)) {
        next.delete(response.id)
      } else {
        next.add(response.id)
      }
      return next
    })
  }

  const visibleResponses = responsesState.items

  const handleSelectAll = () => {
    setSelectedResponseIds(new Set(visibleResponses.map((r) => r.id)))
  }

  const handleUnselectAll = () => {
    setSelectedResponseIds(new Set())
  }

  const handleAttachResponses = () => {
    if (!activeDrillForm) return
    const selectedResponses = visibleResponses.filter((r) => selectedResponseIds.has(r.id))
    if (selectedResponses.length === 0) {
      handleAttachFormOnly(activeDrillForm)
    } else {
      const context = formResponsesToDataContext(activeDrillForm, selectedResponses)
      onToggle(context)
    }
    handleBack()
  }

  const formatDate = (ts: unknown): string => {
    if (!ts) return t('NLSAiContextPickerUnknownDate')
    try {
      if (typeof ts === 'object' && ts !== null && 'toDate' in ts) {
        return (ts as { toDate(): Date }).toDate().toLocaleDateString()
      }
      if (typeof ts === 'number') return new Date(ts).toLocaleDateString()
      return new Date(String(ts)).toLocaleDateString()
    } catch {
      return t('NLSAiContextPickerUnknownDate')
    }
  }

  if (step === 'responses' && activeDrillForm) {
    const formContextId = `workspace-form-responses:${activeDrillForm.id}`
    const isFormResponsesInBasket = basketIdSet.has(formContextId)

    return (
      <div className="ctxPane ctxPane--forms">
        <div className="ctxPane__toolbar">
          <Button
            size="sm"
            variant="tertiary"
            onClick={handleBack}
            icon={<ArrowBackRoundedIcon fontSize="small" />}
          >
            {t('NLSAiContextPickerBackToForms')}
          </Button>
          <div className="ctxPane__drillTitle">
            <DynamicFormRoundedIcon fontSize="small" />
            <strong>{activeDrillForm.title || t('NLSAiContextPickerUntitledForm')}</strong>
          </div>
        </div>

        <div className="ctxPane__toolbar ctxPane__toolbar--secondary">
          <TextInput
            type="search"
            leadingIcon={<SearchRoundedIcon fontSize="small" />}
            placeholder={t('NLSAiContextPickerSearchResponsesPlaceholder')}
            value={responseSearch}
            onChange={setResponseSearch}
            aria-label={t('NLSAiContextPickerSearchResponsesPlaceholder')}
          />
          <div className="ctxPane__selectActions">
            <Button
              size="sm"
              variant="tertiary"
              onClick={handleSelectAll}
              disabled={visibleResponses.length === 0}
            >
              {t('NLSAiContextPickerSelectAll')}
            </Button>
            <Button
              size="sm"
              variant="tertiary"
              onClick={handleUnselectAll}
              disabled={selectedResponseIds.size === 0}
            >
              {t('NLSAiContextPickerUnselectAll')}
            </Button>
          </div>
        </div>

        {responsesState.loading ? (
          <div className="ctxPane__loader">{t('NLSAiContextPickerLoadingResponses')}</div>
        ) : visibleResponses.length === 0 ? (
          <div className="ctxPane__empty">
            <PersonRoundedIcon style={{ fontSize: 32, opacity: 0.3 }} />
            <p>{t('NLSAiContextPickerNoResponses')}</p>
          </div>
        ) : (
          <div className="ctxPane__responseList">
            {visibleResponses.map((response) => {
              const isSelected = selectedResponseIds.has(response.id)
              return (
                <div
                  key={response.id}
                  className={`ctxPane__responseRow${isSelected ? ' is-selected' : ''}`}
                  onClick={() => toggleResponse(response)}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault()
                      toggleResponse(response)
                    }
                  }}
                >
                  <div className="ctxPane__responseCheckbox">
                    {isSelected && <CheckRoundedIcon style={{ fontSize: 12 }} />}
                  </div>
                  <div className="ctxPane__responseInfo">
                    <strong>{response.submittedByName || response.submittedBy || t('NLSAiContextPickerAnonymous')}</strong>
                    <small>{formatDate(response.submittedAt)} · {response.status}</small>
                  </div>
                </div>
              )
            })}
            {responsesState.hasMore && (
              <Button
                size="sm"
                variant="tertiary"
                className="ctxPane__loadMore"
                onClick={responsesState.loadMore}
              >
                {t('NLSAiContextPickerLoadMoreResponses')}
              </Button>
            )}
          </div>
        )}

        <div className="ctxPane__drillFooter">
          <span className="ctxPane__drillCount">
            {selectedResponseIds.size > 0
              ? (selectedResponseIds.size === 1
                  ? t('NLSAiContextPickerResponseSelectedOne')
                  : t('NLSAiContextPickerResponsesSelected', { count: selectedResponseIds.size }))
              : t('NLSAiContextPickerNoResponsesAttachSchema')}
          </span>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAttachResponses}
            pressed={isFormResponsesInBasket}
          >
            {isFormResponsesInBasket ? t('NLSAiContextPickerUpdate') : t('NLSAiContextPickerAttach')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="ctxPane ctxPane--forms">
      <div className="ctxPane__toolbar">
        <TextInput
          type="search"
          leadingIcon={<SearchRoundedIcon fontSize="small" />}
          placeholder={t('NLSAiContextPickerSearchForms')}
          value={search}
          onChange={setSearch}
          aria-label={t('NLSAiContextPickerSearchForms')}
        />
      </div>

      {formsPage.loading ? (
        <div className="ctxPane__loader">{t('NLSAiContextPickerLoadingForms')}</div>
      ) : filteredForms.length === 0 ? (
        <div className="ctxPane__empty">
          <DynamicFormRoundedIcon style={{ fontSize: 36, opacity: 0.3 }} />
          <p>{formsPage.items.length === 0 ? t('NLSAiContextPickerNoForms') : t('NLSAiContextPickerNoFormsMatch')}</p>
        </div>
      ) : (
        <div className="ctxPane__formList">
          {filteredForms.map((form) => {
            const formContextId = `workspace-form:${form.id}`
            const isItemAttachmentMode = mode === 'itemAttachment'
            const isAttached = isItemAttachmentMode
              ? Boolean(attachedFormIds?.has(form.id))
              : basketIdSet.has(formContextId)
            const responsesContextId = `workspace-form-responses:${form.id}`
            const hasResponsesAttached = isItemAttachmentMode ? false : basketIdSet.has(responsesContextId)

            const handleRowClick = async () => {
              if (isItemAttachmentMode) {
                if (isAttached) {
                  await onDetachForm?.(form.id)
                } else {
                  await onAttachForm?.(form.id)
                }
              }
            }

            return (
              <div
                key={form.id}
                className={`ctxPane__formRow${isAttached || hasResponsesAttached ? ' is-selected' : ''}${isItemAttachmentMode ? ' is-clickable' : ''}`}
                onClick={() => void handleRowClick()}
                role={isItemAttachmentMode ? 'checkbox' : undefined}
                aria-checked={isItemAttachmentMode ? isAttached : undefined}
                tabIndex={isItemAttachmentMode ? 0 : undefined}
                onKeyDown={isItemAttachmentMode ? (e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault()
                    void handleRowClick()
                  }
                } : undefined}
              >
                <div className="ctxPane__formRowIcon">
                  <AssignmentRoundedIcon fontSize="small" />
                </div>
                <div className="ctxPane__formRowInfo">
                  <strong>{form.title || t('NLSAiContextPickerUntitledForm')}</strong>
                  <small>
                    {form.lifecycleStatus} · {form.fields.length} field{form.fields.length === 1 ? '' : 's'} · {form.submissionCount} response{form.submissionCount === 1 ? '' : 's'}
                  </small>
                </div>
                {isItemAttachmentMode ? (
                  <div className="ctxPane__formCheckbox">
                    {isAttached && <CheckRoundedIcon style={{ fontSize: 13 }} />}
                  </div>
                ) : (
                  <div className="ctxPane__formRowActions">
                    <Button
                      size="sm"
                      variant="tertiary"
                      onClick={() => handleAttachFormOnly(form)}
                      title={t('NLSAiContextPickerAttachSchemaTitle')}
                      pressed={isAttached}
                    >
                      {t('NLSAiContextPickerSchema')}
                    </Button>
                    <Button
                      size="sm"
                      variant="tertiary"
                      onClick={() => handleDrillIn(form)}
                      title={t('NLSAiContextPickerPickResponsesTitle')}
                      disabled={form.submissionCount === 0}
                      pressed={hasResponsesAttached}
                    >
                      {t('NLSAiContextPickerResponses')}
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
          {formsPage.hasMore && (
            <Button
              size="sm"
              variant="tertiary"
              className="ctxPane__loadMore"
              onClick={formsPage.loadMore}
            >
              {t('NLSAiContextPickerLoadMoreForms')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
